import React, { useCallback } from 'react';

import { Analytics } from 'src/lib/analytics';

interface EventProperties {
  category?: 'Studio';
  label?: string;
  value?: number;
}

type ActionName =
  | 'Hotkey Format Operation'
  | 'Tab to'
  | 'Navigate Docs'
  | 'Click Doc Path'
  | 'Click Doc Back'
  | 'Expand Variables'
  | 'Collapse Variables'
  | 'Toggle Field On'
  | 'Toggle Field Off'
  | 'Toggle Argument On'
  | 'Toggle Argument Off'
  | 'Search Session Start'
  | 'Search Session Cancel'
  | 'Search Session Complete'
  | 'Search Session Keyboard NavCount'
  | 'Search Session End Duration'
  | 'Search Session Choose Path'
  | 'Hotkey Open Command Palette';

export const AnalyticsContext = React.createContext<ReturnType<
  typeof useAnalytics
> | null>(null);

export const AnalyticsProvider = ({
  children,
  defaultCategory,
  userId,
}: {
  children: React.ReactNode;
  defaultCategory?: string;
  userId?: string;
}) => {
  const analytics = useAnalytics({ defaultCategory, userId });
  return (
    <AnalyticsContext.Provider value={analytics}>
      {children}
    </AnalyticsContext.Provider>
  );
};

/**
 * @deprecated: switch to useTrackCustomEvent
 */
export const useAnalyticsContext = () => {
  const context = React.useContext(AnalyticsContext);
  if (!context) {
    throw new Error(
      'useAnalyticsContext must be used within an AnalyticsProvider',
    );
  }
  return context;
};

const useAnalytics = ({
  defaultCategory,
  userId,
}: {
  defaultCategory?: string;
  userId?: string;
}) => {
  const track = useCallback(
    (eventName: ActionName, eventProperties?: EventProperties) => {
      Analytics.track(userId || '', eventName, {
        category: defaultCategory,
        ...eventProperties,
      });
    },
    [userId, defaultCategory],
  );

  return React.useMemo(() => ({ track }), [track]);
};
